import type { ReactNode } from 'react';
import type { Training } from '../../services/training.service';

export function TrainingModuleCard({
  training,
  onOpen,
  thumbnailLeftSlot,
  thumbnailRightSlot,
  actionsSlot,
  showOrder = false,
}: {
  training: Training;
  onOpen?: () => void;
  thumbnailLeftSlot?: ReactNode;
  thumbnailRightSlot?: ReactNode;
  actionsSlot?: ReactNode;
  showOrder?: boolean;
}) {
  const lessonCount = training._count?.lessons ?? 0;
  const thumbUrl = training.thumbnailPresignedUrl || training.thumbnailUrl;

  return (
    <div className="group overflow-hidden rounded-2xl border border-outline-variant/10 bg-surface-container-low transition-all hover:-translate-y-1 hover:border-outline-variant/25 hover:shadow-lg">
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="relative flex aspect-video w-full items-center justify-center bg-surface-container text-left"
        >
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl primary-gradient">
                <span className="material-symbols-outlined text-2xl text-on-primary">school</span>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
          {thumbnailLeftSlot ? <div className="absolute left-2 top-2">{thumbnailLeftSlot}</div> : null}
          {thumbnailRightSlot ? <div className="absolute bottom-3 right-3 flex gap-1.5">{thumbnailRightSlot}</div> : null}
        </button>
      ) : (
        <div className="relative flex aspect-video items-center justify-center bg-surface-container">
          {thumbUrl ? (
            <img
              src={thumbUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl primary-gradient">
                <span className="material-symbols-outlined text-2xl text-on-primary">school</span>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
          {thumbnailLeftSlot ? <div className="absolute left-2 top-2">{thumbnailLeftSlot}</div> : null}
          {thumbnailRightSlot ? <div className="absolute bottom-3 right-3 flex gap-1.5">{thumbnailRightSlot}</div> : null}
        </div>
      )}

      <div className="space-y-3 p-5">
        {onOpen ? (
          <button type="button" onClick={onOpen} className="w-full text-left">
            <h3 className="line-clamp-1 text-base font-bold text-on-surface transition-colors group-hover:text-primary">
              {training.title}
            </h3>
            {training.description ? (
              <p className="mt-1 line-clamp-2 text-xs text-on-surface-variant">{training.description}</p>
            ) : null}
          </button>
        ) : (
          <div>
            <h3 className="line-clamp-1 text-base font-bold text-on-surface transition-colors group-hover:text-primary">
              {training.title}
            </h3>
            {training.description ? (
              <p className="mt-1 line-clamp-2 text-xs text-on-surface-variant">{training.description}</p>
            ) : null}
          </div>
        )}

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-surface-container px-2.5 py-1 text-[11px] font-semibold text-on-surface-variant">
              {lessonCount} {lessonCount === 1 ? 'lesson' : 'lessons'}
            </span>
            {showOrder ? (
              <span className="text-[11px] text-on-surface-variant/60">order {training.order}</span>
            ) : null}
          </div>
          {actionsSlot}
        </div>
      </div>
    </div>
  );
}
